import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { caregiverProfilesAPI, usersAPI } from '../services/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import OnboardingTab from '../components/onboarding/OnboardingTab';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  UserCircle,
  Plus,
  MagnifyingGlass,
  Phone,
  MapPin,
  Certificate,
  Briefcase,
  ClipboardText
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const CaregiversPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'onboarding' ? 'onboarding' : 'caregivers';
  const setTab = (v) => setSearchParams(v === 'onboarding' ? { tab: 'onboarding' } : {});
  const [profiles, setProfiles] = useState([]);
  const [caregiverUsers, setCaregiverUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [formData, setFormData] = useState({
    user_id: '', name: '', date_of_birth: '', address: '', phone: '',
    emergency_contact: '', emergency_phone: '', certifications: '',
    specializations: '', hire_date: '', employment_status: 'active', notes: '',
    login_email: '', login_password: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [profilesRes, usersRes] = await Promise.all([
        caregiverProfilesAPI.getAll(),
        usersAPI.getAll('caregiver')
      ]);
      setProfiles(profilesRes.data);
      setCaregiverUsers(usersRes.data);
    } catch (error) {
      console.error('Error loading caregivers:', error);
      toast.error('Failed to load caregivers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      if (formData.login_email && formData.login_password && formData.login_password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      if ((formData.login_email && !formData.login_password) || (!formData.login_email && formData.login_password)) {
        toast.error('Both email and password are required to create a login');
        return;
      }
      const data = {
        ...formData,
        certifications: formData.certifications.split(',').map(s => s.trim()).filter(Boolean),
        specializations: formData.specializations.split(',').map(s => s.trim()).filter(Boolean),
        login_email: formData.login_email || null,
        login_password: formData.login_password || null,
      };
      await caregiverProfilesAPI.create(data);
      toast.success(formData.login_email ? 'Caregiver profile + login created' : 'Caregiver profile created');
      setIsCreateOpen(false);
      setFormData({ user_id: '', name: '', date_of_birth: '', address: '', phone: '', emergency_contact: '', emergency_phone: '', certifications: '', specializations: '', hire_date: '', employment_status: 'active', notes: '', login_email: '', login_password: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create caregiver profile');
    }
  };

  const filteredProfiles = profiles.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.specializations?.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = showInactive ? true : p.status !== 'inactive';
    return matchesSearch && matchesStatus;
  });

  const inactiveCount = profiles.filter(p => p.status === 'inactive').length;

  if (user?.role !== 'supervisor') {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <UserCircle weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" />
        <h3 className="font-heading text-lg font-medium">Access Restricted</h3>
        <p className="text-muted-foreground text-sm mt-1">Only supervisors can manage caregivers</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="caregivers-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground">Caregivers</h1>
          <p className="text-muted-foreground mt-1">Manage caregiver profiles and documentation</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className={`rounded-xl gap-2 ${tab === 'onboarding' ? 'hidden' : ''}`} data-testid="add-caregiver-btn">
              <Plus weight="bold" className="w-4 h-4" />
              Add Caregiver
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Create Caregiver Profile</DialogTitle>
              <DialogDescription>Add a new caregiver to the system</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Jane Smith" required className="rounded-xl" data-testid="cg-name-input" />
              </div>

              {/* Login credentials (creates the user account at the same time) */}
              <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Create Login (optional)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Email</Label>
                    <Input type="email" value={formData.login_email} onChange={(e) => setFormData({ ...formData, login_email: e.target.value })} placeholder="caregiver@example.com" className="rounded-xl" data-testid="cg-login-email-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Temporary Password</Label>
                    <Input type="password" value={formData.login_password} onChange={(e) => setFormData({ ...formData, login_password: e.target.value })} placeholder="Min 6 chars" minLength={6} className="rounded-xl" data-testid="cg-login-password-input" />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground">The caregiver will be prompted to set a new password on first sign-in.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date of Birth</Label>
                  <Input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} className="rounded-xl" data-testid="cg-dob-input" />
                </div>
                <div className="space-y-2">
                  <Label>Hire Date</Label>
                  <Input type="date" value={formData.hire_date} onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })} className="rounded-xl" data-testid="cg-hire-date-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+1 555-0000" className="rounded-xl" data-testid="cg-phone-input" />
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="123 Main St" className="rounded-xl" data-testid="cg-address-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Emergency Contact</Label>
                  <Input value={formData.emergency_contact} onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })} placeholder="John Smith (Spouse)" className="rounded-xl" data-testid="cg-ec-input" />
                </div>
                <div className="space-y-2">
                  <Label>Emergency Phone</Label>
                  <Input value={formData.emergency_phone} onChange={(e) => setFormData({ ...formData, emergency_phone: e.target.value })} placeholder="+1 555-0000" className="rounded-xl" data-testid="cg-ep-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Certifications (comma-separated)</Label>
                <Input value={formData.certifications} onChange={(e) => setFormData({ ...formData, certifications: e.target.value })} placeholder="CNA, CPR, First Aid" className="rounded-xl" data-testid="cg-certs-input" />
              </div>
              <div className="space-y-2">
                <Label>Specializations (comma-separated)</Label>
                <Input value={formData.specializations} onChange={(e) => setFormData({ ...formData, specializations: e.target.value })} placeholder="Elderly Care, Dementia, Mobility" className="rounded-xl" data-testid="cg-specs-input" />
              </div>
              <div className="space-y-2">
                <Label>Employment Status</Label>
                <Select value={formData.employment_status} onValueChange={(v) => setFormData({ ...formData, employment_status: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="cg-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="on_leave">On Leave</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes..." className="rounded-xl" data-testid="cg-notes-input" />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                <Button type="submit" className="flex-1 rounded-xl" data-testid="create-cg-submit">Create Profile</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
      <TabsList className="rounded-xl">
        <TabsTrigger value="caregivers" className="rounded-lg gap-1.5" data-testid="tab-caregivers"><UserCircle className="w-4 h-4" /> Caregivers</TabsTrigger>
        <TabsTrigger value="onboarding" className="rounded-lg gap-1.5" data-testid="tab-onboarding"><ClipboardText className="w-4 h-4" /> Onboarding</TabsTrigger>
      </TabsList>
      <TabsContent value="onboarding"><OnboardingTab /></TabsContent>
      <TabsContent value="caregivers" className="space-y-6">
      <div className="flex items-center gap-3 max-w-2xl flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input placeholder="Search caregivers..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 rounded-xl" data-testid="cg-search-input" />
        </div>
        <Button
          variant={showInactive ? 'default' : 'outline'}
          className="rounded-xl text-sm"
          onClick={() => setShowInactive(!showInactive)}
          data-testid="toggle-inactive-btn"
        >
          {showInactive ? 'Hide Inactive' : `Show Inactive${inactiveCount > 0 ? ` (${inactiveCount})` : ''}`}
        </Button>
      </div>

      {filteredProfiles.length === 0 ? (
        <Card className="rounded-2xl border border-border">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <UserCircle weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="font-heading text-lg font-medium">No caregivers found</h3>
            <p className="text-muted-foreground text-sm mt-1">{searchTerm ? 'Try a different search' : 'Add your first caregiver'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProfiles.map(cg => (
            <Card key={cg.id} className="rounded-2xl border border-border hover:border-primary/30 hover:shadow-md transition-all duration-200 cursor-pointer" onClick={() => navigate(`/caregivers/${cg.id}`)} data-testid={`cg-card-${cg.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-600/10 flex items-center justify-center">
                      <UserCircle weight="duotone" className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="font-heading text-lg">{cg.name}</CardTitle>
                      {cg.hire_date && <CardDescription>Hired: {cg.hire_date}</CardDescription>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className={`rounded-full text-xs ${cg.employment_status === 'active' ? 'bg-green-100 text-green-800' : cg.employment_status === 'on_leave' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>
                      {cg.employment_status?.replace('_', ' ')}
                    </Badge>
                    {cg.status === 'inactive' && (
                      <Badge className="rounded-full text-xs bg-red-100 text-red-800">INACTIVE</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {cg.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone weight="duotone" className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{cg.phone}</span>
                  </div>
                )}
                {cg.address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin weight="duotone" className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <span className="text-muted-foreground">{cg.address}</span>
                  </div>
                )}
                {cg.certifications?.length > 0 && (
                  <div className="flex items-start gap-2">
                    <Certificate weight="duotone" className="w-4 h-4 text-muted-foreground mt-1" />
                    <div className="flex flex-wrap gap-1">
                      {cg.certifications.map((c, i) => (
                        <Badge key={i} variant="secondary" className="text-xs rounded-full">{c}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {cg.specializations?.length > 0 && (
                  <div className="flex items-start gap-2">
                    <Briefcase weight="duotone" className="w-4 h-4 text-muted-foreground mt-1" />
                    <div className="flex flex-wrap gap-1">
                      {cg.specializations.map((s, i) => (
                        <Badge key={i} className="text-xs rounded-full bg-blue-100 text-blue-800">{s}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </TabsContent>
      </Tabs>
    </div>
  );
};

export default CaregiversPage;
